/**
 * Editor Top Bar — Premium Visual Editor Chrome
 *
 * World-class toolbar: clean, spacious, with intentional visual hierarchy.
 * Dropdown page selector supports all page types (grouped by category).
 * All pages render from JSON via EditableRenderer for full editing support.
 */

import React, { useState, useEffect } from 'react';
import {
  Save, Undo2, Redo2, Monitor, Smartphone, Tablet,
  Eye, Pencil, Palette, ArrowLeft, Loader2, ExternalLink,
  Building2, ChevronDown, Layers, Clock, Check, ShoppingBag,
  Plus, Minus, Maximize2, Sparkles
} from 'lucide-react';

const PAGE_OPTIONS = [
  { value: 'main', label: 'Home Page', group: 'Main Pages' },
  { value: 'shop', label: 'Shop Page', group: 'Main Pages' },
  { value: 'product', label: 'Product Page', group: 'Main Pages' },
  { value: 'about', label: 'About Page', group: 'Main Pages' },
  { value: 'contact', label: 'Contact Page', group: 'Main Pages' },
  { value: 'navbar', label: 'Navigation Bar', group: 'Site-wide' },
  { value: 'footer', label: 'Footer', group: 'Site-wide' },
  { value: 'privacy', label: 'Privacy Policy', group: 'Info Pages' },
  { value: 'shipping', label: 'Shipping & Returns', group: 'Info Pages' },
  { value: 'terms', label: 'Terms & Conditions', group: 'Info Pages' },
  { value: 'faqs', label: 'FAQs', group: 'Info Pages' },
];

const EditorTopBar = ({
  templateName,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  onSaveDraft,
  isSaving,
  isDraftSaving,
  isDirty,
  editMode,
  onToggleEditMode,
  previewDevice,
  onSetDevice,
  showThemePanel,
  onToggleThemePanel,
  showBusinessPanel,
  onToggleBusinessPanel,
  showSectionLibrary,
  onToggleSectionLibrary,
  showVersionHistory,
  onToggleVersionHistory,
  showAnimationPanel,
  onToggleAnimationPanel,
  showProductPicker,
  onToggleProductPicker,
  productCount,
  onBack,
  onPreviewLive,
  currentPage,
  onPageChange,
  autoSaveStatus,
  zoomLevel,
  onZoomChange,
  onReset,
}) => {
  const [saveFlash, setSaveFlash] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Flash effect after save completes
  useEffect(() => {
    if (!isSaving && justSaved) {
      setSaveFlash(true);
      const t = setTimeout(() => setSaveFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isSaving, justSaved]);

  const handleSave = () => {
    setJustSaved(true);
    onSave();
  };

  // Reset justSaved when dirty again
  useEffect(() => {
    if (isDirty) setJustSaved(false);
  }, [isDirty]);

  return (
    <div style={{
      height: '48px',
      background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      gap: '8px',
      flexShrink: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      position: 'relative',
      zIndex: 50,
    }}>

      {/* ── Left: Back + Template name + Page selector ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flexShrink: 0, position: 'relative', zIndex: 2 }}>
        <button
          onClick={onBack}
          style={{
            width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fafafa', cursor: 'pointer',
            color: '#6b7280', transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.transform = 'scale(1.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'scale(1)'; }}
          title="Back to templates"
        >
          <ArrowLeft style={{ width: '16px', height: '16px' }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span style={{
            fontSize: '15px', fontWeight: 700, color: '#111827',
            maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            letterSpacing: '-0.02em',
          }}>
            {templateName || 'Template Editor'}
          </span>
          {isDirty && (
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              boxShadow: '0 0 6px rgba(245,158,11,0.5)',
              animation: 'pulse 2s ease-in-out infinite',
            }} title="Unsaved changes" />
          )}
          {saveFlash && !isDirty && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              fontSize: '11px', fontWeight: 600, color: '#059669',
              animation: 'fadeInUp 0.3s ease-out',
            }}>
              <Check style={{ width: '12px', height: '12px' }} />
              Saved
            </span>
          )}
        </div>

        {/* Page selector — dropdown */}
        {onPageChange && (
          <>
            <div style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />
            <div style={{ position: 'relative' }}>
              <select
                value={currentPage || 'main'}
                onChange={(e) => onPageChange(e.target.value)}
                style={{
                  padding: '6px 30px 6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #e2e5ea',
                  background: '#f8f9fb',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#111827',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
                  transition: 'all 0.2s ease',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                }}
              >
                {(() => {
                  const groups = {};
                  PAGE_OPTIONS.forEach(opt => {
                    if (!groups[opt.group]) groups[opt.group] = [];
                    groups[opt.group].push(opt);
                  });
                  return Object.entries(groups).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
            </div>
          </>
        )}
      </div>

      {/* ── Center: Undo/Redo + Device + Edit/Preview ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>

        {/* Undo / Redo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <ToolbarIconButton
            icon={Undo2}
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          />
          <ToolbarIconButton
            icon={Redo2}
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
          />
        </div>

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 6px' }} />

        {/* Device toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '10px', padding: '3px',
        }}>
          {[
            { device: 'desktop', Icon: Monitor, label: 'Desktop' },
            { device: 'tablet', Icon: Tablet, label: 'Tablet' },
            { device: 'mobile', Icon: Smartphone, label: 'Mobile' },
          ].map(({ device, Icon, label }) => {
            const isActive = previewDevice === device;
            return (
              <button
                key={device}
                onClick={() => onSetDevice(device)}
                style={{
                  width: '36px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: isActive ? '#eef2ff' : 'transparent',
                  color: isActive ? '#4f46e5' : '#9ca3af',
                  boxShadow: isActive ? '0 1px 4px rgba(99,102,241,0.15)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                title={`${label} view`}
              >
                <Icon style={{ width: '16px', height: '16px' }} />
              </button>
            );
          })}
        </div>

        {/* Zoom controls */}
        {onZoomChange && (
          <>
            <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <ToolbarIconButton
                icon={Minus}
                onClick={() => onZoomChange(Math.max(25, (zoomLevel || 100) - 25))}
                disabled={(zoomLevel || 100) <= 25}
                title="Zoom out (Ctrl+−)"
              />
              <button
                onClick={() => onZoomChange(100)}
                style={{
                  padding: '3px 6px', borderRadius: '6px', border: 'none',
                  background: (zoomLevel || 100) !== 100 ? '#f0f9ff' : 'transparent',
                  color: (zoomLevel || 100) !== 100 ? '#3b82f6' : '#6b7280',
                  fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  minWidth: '40px', textAlign: 'center',
                  transition: 'all 0.15s',
                }}
                title="Reset zoom (Ctrl+0)"
              >
                {zoomLevel || 100}%
              </button>
              <ToolbarIconButton
                icon={Plus}
                onClick={() => onZoomChange(Math.min(200, (zoomLevel || 100) + 25))}
                disabled={(zoomLevel || 100) >= 200}
                title="Zoom in (Ctrl+=)"
              />
            </div>
          </>
        )}

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />

        {/* Edit / Preview toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '10px', padding: '3px',
        }}>
          <button
            onClick={() => { if (!editMode) onToggleEditMode(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: editMode ? 600 : 500,
              background: editMode ? '#ffffff' : 'transparent',
              color: editMode ? '#111827' : '#9ca3af',
              boxShadow: editMode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <Pencil style={{ width: '12px', height: '12px' }} />
            Edit
          </button>
          <button
            onClick={() => { if (editMode) onToggleEditMode(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: !editMode ? 600 : 500,
              background: !editMode ? '#ffffff' : 'transparent',
              color: !editMode ? '#111827' : '#9ca3af',
              boxShadow: !editMode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <Eye style={{ width: '12px', height: '12px' }} />
            Preview
          </button>
        </div>
      </div>

      {/* ── Right: Panels (grouped icon bar) + Preview + Save ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

        <div style={{ display: 'flex', gap: '2px', backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '3px' }}>
          {[
            { key: 'brand', icon: Building2, label: 'Brand', active: showBusinessPanel, handler: onToggleBusinessPanel, show: true },
            { key: 'theme', icon: Palette, label: 'Theme', active: showThemePanel, handler: onToggleThemePanel, show: true },
            { key: 'sections', icon: Layers, label: 'Sections', active: showSectionLibrary, handler: onToggleSectionLibrary, show: !!onToggleSectionLibrary },
            { key: 'animate', icon: Sparkles, label: 'Animate', active: showAnimationPanel, handler: onToggleAnimationPanel, show: !!onToggleAnimationPanel },
            { key: 'products', icon: ShoppingBag, label: 'Products', active: showProductPicker, handler: onToggleProductPicker, show: !!onToggleProductPicker, badge: productCount },
            { key: 'history', icon: Clock, label: 'History', active: showVersionHistory, handler: onToggleVersionHistory, show: !!onToggleVersionHistory },
          ].filter(btn => btn.show).map(btn => (
            <button
              key={btn.key}
              onClick={btn.handler}
              title={btn.label}
              style={{
                padding: '6px 10px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                backgroundColor: btn.active ? 'white' : 'transparent',
                boxShadow: btn.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px',
                color: btn.active ? '#d45614' : '#6b7280', fontWeight: btn.active ? 600 : 400,
                transition: 'all 0.15s ease',
              }}
            >
              <btn.icon size={15} />
              <span className="hidden xl:inline">{btn.label}</span>
              {btn.badge > 0 && <span style={{ fontSize: '10px', backgroundColor: '#fef3c7', color: '#92400e', padding: '0 5px', borderRadius: '8px', fontWeight: 700 }}>{btn.badge}</span>}
            </button>
          ))}
        </div>

        {onPreviewLive && (
          <ToolbarIconButton
            icon={ExternalLink}
            onClick={onPreviewLive}
            title="Preview live site"
          />
        )}

        {/* Reset to original template */}
        {onReset && (
          <button onClick={onReset} title="Reset to original template" style={{
            padding: '4px 8px', borderRadius: '6px', border: '1px solid #fecaca',
            backgroundColor: '#fef2f2', cursor: 'pointer', fontSize: '11px',
            color: '#dc2626', fontWeight: 500, transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
          >
            Reset
          </button>
        )}

        {/* Keyboard shortcuts help */}
        <button
          onClick={() => setShowShortcuts(true)}
          title="Keyboard Shortcuts"
          style={{
            width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #e2e8f0',
            backgroundColor: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
            color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = '#6b7280'; }}
        >
          ?
        </button>

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />

        {/* Auto-save status indicator */}
        {autoSaveStatus === 'saving' && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', color: '#9ca3af', fontWeight: 500,
            marginRight: '4px', animation: 'fadeInUp 0.3s ease-out',
          }}>
            <Loader2 style={{ width: '12px', height: '12px', animation: 'spin 0.8s linear infinite' }} />
            Auto-saving...
          </span>
        )}
        {autoSaveStatus === 'saved' && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', color: '#9ca3af', fontWeight: 500,
            marginRight: '4px', animation: 'fadeInUp 0.3s ease-out',
          }}>
            <Check style={{ width: '12px', height: '12px', color: '#059669' }} />
            All changes saved
          </span>
        )}

        {/* Save button — single CTA (no draft) */}
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 16px', borderRadius: '8px', border: 'none',
            cursor: isSaving || !isDirty ? 'default' : 'pointer',
            fontSize: '13px', fontWeight: 600,
            background: isDirty
              ? 'linear-gradient(135deg, #d45614, #e8774a)'
              : '#e5e7eb',
            color: isDirty ? '#ffffff' : '#9ca3af',
            boxShadow: isDirty && !isSaving
              ? '0 2px 8px rgba(212,86,20,0.3)'
              : 'none',
            transition: 'all 0.2s ease',
            transform: isSaving ? 'scale(0.97)' : 'scale(1)',
          }}
          onMouseEnter={e => {
            if (isDirty && !isSaving) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #c04a10, #d45614)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(212,86,20,0.4)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={e => {
            if (isDirty && !isSaving) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #d45614, #e8774a)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(212,86,20,0.3)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          {isSaving ? (
            <><Loader2 style={{ width: '14px', height: '14px', animation: 'spin 0.8s linear infinite' }} /> Saving...</>
          ) : !isDirty ? (
            <><Check style={{ width: '14px', height: '14px' }} /> Saved</>
          ) : (
            <><Save style={{ width: '14px', height: '14px' }} /> Save</>
          )}
        </button>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99990, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowShortcuts(false)}
        >
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: 'min(400px, 90vw)', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#1f2937' }}>Keyboard Shortcuts</h3>
            {[
              ['Ctrl + S', 'Save changes'],
              ['Ctrl + Z', 'Undo'],
              ['Ctrl + Y', 'Redo'],
              ['Ctrl + D', 'Duplicate element'],
              ['Ctrl + B', 'Bold text'],
              ['Ctrl + I', 'Italic text'],
              ['Ctrl + U', 'Underline text'],
              ['Delete', 'Delete selected element'],
              ['Escape', 'Deselect element'],
              ['Ctrl + +', 'Zoom in'],
              ['Ctrl + -', 'Zoom out'],
            ].map(([key, desc]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{desc}</span>
                <kbd style={{ fontSize: '12px', padding: '2px 8px', backgroundColor: '#f3f4f6', borderRadius: '4px', fontFamily: 'monospace', color: '#374151' }}>{key}</kbd>
              </div>
            ))}
            <button onClick={() => setShowShortcuts(false)} style={{
              marginTop: '16px', width: '100%', padding: '10px', borderRadius: '10px',
              border: 'none', backgroundColor: '#f3f4f6', cursor: 'pointer', fontWeight: 600,
              fontSize: '14px', color: '#374151',
            }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

/* ── Reusable toolbar icon button ── */
function ToolbarIconButton({ icon: Icon, onClick, disabled, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '9px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: hovered && !disabled ? '#f3f4f6' : 'transparent',
        color: disabled ? '#d1d5db' : (hovered ? '#111827' : '#6b7280'),
        transition: 'all 0.15s ease',
      }}
    >
      <Icon style={{ width: '16px', height: '16px' }} />
    </button>
  );
}

export default EditorTopBar;
